import WatsonApi from "../../../backends/WatsonApi";
import NotificationManager from "../../notifications/NotificationManager";
import { useStores } from "../../../stores/useStores";
import styles from "../styles/PeopleCollectionManagementModal.module.scss";
import { runInAction } from "mobx";
import { observer } from "mobx-react";
import { useOptimistic, useTransition } from "react";
import ToggleSwitch from "../../admin/sidebar/items/ToggleSwitch";
import { useFeatureFlags } from "../../../utils/featureFlags/useFeatureFlags";
import classNames from "classnames";

export const ContactTypeSelection = observer(() => {
  const { organizationStore } = useStores();
  const staffDirectoryContactType =
    organizationStore.organization.json_data.settings.appearance
      .staffDirectoryContactType;
  const [, startTransition] = useTransition();
  const [optimisticStaffDirectoryContactType, addOptimistic] = useOptimistic(
    staffDirectoryContactType,
    (currentState, optimisticValue) => optimisticValue,
  );
  const { show_two_way_messaging } = useFeatureFlags();

  const toggleEmailDisplay = async (e) => {
    let newValue = e.target.value;
    if (newValue === "null") {
      newValue = null;
    }

    startTransition(async () => {
      try {
        addOptimistic(newValue);

        const client = await WatsonApi();

        const data = {
          appearance: {
            staffDirectoryContactType: newValue,
          },
        };

        await client.apis.settings.settings_update({
          id: organizationStore.organization.id,
          data: data,
        });

        runInAction(() => {
          organizationStore.organization.json_data.settings.appearance.staffDirectoryContactType =
            newValue;
        });
        NotificationManager.success(
          "Email display setting updated successfully!",
        );
      } catch (e) {
        NotificationManager.error("Failed to update email display setting.");
        console.error(e);
      }
    });
  };

  const handleChange = async () => {
    try {
      const client = await WatsonApi();

      const data = {
        appearance: {
          preferDirectMessaging:
            !organizationStore.organization.json_data.settings.appearance
              .preferDirectMessaging,
        },
      };

      await client.apis.settings.settings_update({
        id: organizationStore.organization.id,
        data: data,
      });

      runInAction(() => {
        organizationStore.organization.json_data.settings.appearance.preferDirectMessaging =
          !organizationStore.organization.json_data.settings.appearance
            .preferDirectMessaging;
      });

      NotificationManager.success("Preferred message status has been updated");
    } catch (e) {
      if (process.env.NEXT_PUBLIC_APP_ENV === "dev") {
        console.error(JSON.stringify(e));
      }
      NotificationManager.error(
        "There was an error when updating the preferred message status",
      );
    }
  };

  return (
    <div>
      <div className={styles.contactOptionsContainer}>
        <h3>Staff Contact Options</h3>
        <div>
          <input
            type={"radio"}
            value={"null"}
            checked={optimisticStaffDirectoryContactType === null}
            onChange={toggleEmailDisplay}
            id={"staffDirectoryContactTypeNull"}
          />
          <label htmlFor={"staffDirectoryContactTypeNull"}>
            No Email or Contact Form
          </label>
          <small>
            Your email will not be shown, and visitors will not have a way to
            contact you through the website.
          </small>
        </div>
        <div>
          <input
            type={"radio"}
            value={"email"}
            checked={optimisticStaffDirectoryContactType === "email"}
            onChange={toggleEmailDisplay}
            id={"staffDirectoryContactTypeEmail"}
          />
          <label htmlFor={"staffDirectoryContactTypeEmail"}>Show Email</label>
          <small>
            Emails are obfuscated when users are not logged in. When the user
            clicks on it, the email is revealed. When logged in, the email is
            shown by default.
          </small>
        </div>
        <div>
          <input
            type={"radio"}
            value={"form"}
            checked={optimisticStaffDirectoryContactType === "form"}
            onChange={toggleEmailDisplay}
            id={"staffDirectoryContactTypeForm"}
          />
          <label htmlFor={"staffDirectoryContactTypeForm"}>
            Contact Form (Email Hidden)
          </label>
          <small>
            Email addresses will stay private, but visitors can send messages
            through a contact form. The form will deliver messages directly to
            that staff member&#39;s inbox without showing the email publicly.
          </small>
        </div>
      </div>
      {/*TODO: hide behind 2 way billing product*/}
      {show_two_way_messaging && (
        <div
          className={classNames(
            styles.contactOptionsContainer,
            styles.directMessaging,
          )}
        >
          <ToggleSwitch
            enabled={true}
            on={
              organizationStore.organization.json_data.settings.appearance
                .preferDirectMessaging
            }
            onClick={handleChange}
            aria-label={"Toggle preferred method for contacting teachers."}
            id={"preferDirectMessaging"}
          />
          <label htmlFor={"preferDirectMessaging"}>Direct Messaging</label>
          <small>
            Prefer Direct Messaging: Use this site&#39;s built-in message inbox
            to receive messages from parent and student visitors. For everyone
            else, fallback to selected Staff Contact Option.
          </small>
        </div>
      )}
    </div>
  );
});
